import { Button } from "@/components/button";
import { Combobox } from "@/components/combobox";
import CalendarDateRangePicker from "@/components/data-range-picker";
import { Label } from "@/components/label";
import { LoadingSpinner } from "@/components/loading-spinner";
import QuestionMarkTooltip from "@/components/questionmark-tooltip";
import { Separator } from "@/components/separator";
import { ToggleGroup, ToggleGroupItem } from "@/components/toggle-group";
import { CalendarDates } from "@/custom-types";
import { useAudienceAndCampaignOptions, usePerformance } from "@/http/api-hooks";
import { CampaignType, CampaignTypeValuesInstallOnly } from "@/http/api-types";
import { useUrlParams } from "@/http/use-url-params";
import { campaignTypeLabels } from "@/lib/mappings";
import { useFilterTracking } from "@/lib/mixpanel";
import { formatDateToYYYYMMDD, getAudienceSelectOptions, getCampaignSelectOptions } from "@/lib/utils";
import { addDays } from "date-fns";
import { useState } from "react";

import RevenueByAdPlacementTypeBarChart from "./ad-placement-type/revenue-by-ad-placement-type-bar-chart";
import ConversionAvailabilityChart from "./conversion/conversion-availability-chart";
import ConversionTimingHistogramChart from "./conversion/conversion-timing-histogram-chart";
import CPIAchievement from "./cpi/cpi";
import PerformanceMetricCards from "./performance-metric-cards";

import ChartTemplate from "@/components/chart-template";
import { calculateAvailabilityStats, calculateRevenueAggregates } from "./performance-calculations";
import RevenueData from "./revenue/revenue-data";
import SecondaryCPAAchievement from "./secondary-cpa/secondary-cpa";

function Performance() {
  const { paramState, updateParam, updateDateRange, hasUrlParams } = useUrlParams({
    audience: {
      name: "audience",
      type: "string",
      defaultValue: "",
    },
    campaign: {
      name: "campaign",
      type: "string",
      defaultValue: "",
    },
    campaignType: {
      name: "campaign_type",
      type: "enum",
      validValues: CampaignTypeValuesInstallOnly as readonly string[],
      defaultValue: "all" as CampaignType,
    },
    fromDate: {
      name: "from",
      type: "date",
      defaultValue: addDays(new Date(), -14),
    },
    toDate: {
      name: "to",
      type: "date",
      defaultValue: new Date(),
    },
  });

  const currentDate = {
    from: paramState.fromDate || addDays(new Date(), -14),
    to: paramState.toDate || new Date(),
  };

  const audience = paramState.audience;
  const campaign = paramState.campaign;

  const { trackFilterChange } = useFilterTracking();

  const isReadyToFetch = hasUrlParams && currentDate?.from && currentDate?.to && audience !== "";
  const [readyToFetch, setReadyToFetch] = useState<boolean>(isReadyToFetch);

  const { data: audienceCampaignData, isLoading: audienceOptionsLoading } = useAudienceAndCampaignOptions({
    campaign_type: paramState.campaignType as string,
  });

  const buttonDisabled = !audience || !currentDate?.from || !currentDate?.to;

  const shouldFetch = !buttonDisabled && readyToFetch;
  const {
    data: performanceData,
    isLoading: performanceLoading,
    error: performanceError,
  } = usePerformance(
    {
      audience_id: audience,
      start_date: currentDate?.from && formatDateToYYYYMMDD(currentDate.from),
      end_date: currentDate?.to && formatDateToYYYYMMDD(currentDate.to),
      campaign_type: paramState.campaignType as string,
      ...(campaign && { campaign_id: campaign }),
    },
    shouldFetch,
  );

  const audienceOptions = getAudienceSelectOptions(audienceCampaignData);

  const onAudienceChange = (value: string) => {
    trackFilterChange("audience", value);
    updateParam("campaign", "");
    updateParam("audience", value);
  };

  const onCampaignChange = (value: string) => {
    trackFilterChange("campaign", value);
    updateParam("campaign", value);
  };

  const onCampaignTypeChange = (value: string) => {
    trackFilterChange("campaign_type", value);
    updateParam("audience", "");
    updateParam("campaign", "");
    updateParam("campaignType", value);
  };

  const campaignOptions = getCampaignSelectOptions(audienceOptions, audience);

  const handleDateChange = (newDate: CalendarDates) => {
    trackFilterChange("date_range", { from: newDate.from, to: newDate.to });
    updateDateRange(newDate, "fromDate", "toDate");
  };

  const handleShowButtonClick = () => {
    setReadyToFetch(true);
    updateDateRange(currentDate, "fromDate", "toDate");
  };

  const primaryStats = calculateAvailabilityStats(performanceData?.conversion_availability?.results || [], "primary");
  const secondaryStats = calculateAvailabilityStats(
    performanceData?.conversion_availability?.results || [],
    "secondary",
  );

  const getTimingStats = () => {
    if (!performanceData?.timing_histogram?.results?.length) {
      return {
        apAvg: 0,
        apMedian: 0,
        secondaryApAvg: 0,
        secondaryApMedian: 0,
        apTotal: 0,
        secondaryApTotal: 0,
      };
    }

    const firstRow = performanceData.timing_histogram.results[0];
    return {
      apAvg: firstRow.ap_avg_time_seconds,
      apMedian: firstRow.ap_median_time_seconds,
      secondaryApAvg: firstRow.secondary_ap_avg_time_seconds,
      secondaryApMedian: firstRow.secondary_ap_median_time_seconds,
      apTotal: firstRow.ap_total_with_timing,
      secondaryApTotal: firstRow.secondary_ap_total_with_timing,
    };
  };

  const timingStats = getTimingStats();

  const aggregatedRow = calculateRevenueAggregates(performanceData?.revenue?.results || []);

  const hasTargetCpa = (aggregatedRow?.secondary_ap_target_cpx_eur || 0) > 0;

  const showAllAudiencesOption = paramState.campaignType === "ctv_install";

  return (
    <>
      <div className="flex flex-col gap-4 mb-8">
        <div>
          <ToggleGroup
            type="single"
            value={paramState.campaignType as string}
            onValueChange={onCampaignTypeChange}
            variant="outline"
          >
            <ToggleGroupItem value="all" className="min-w-[100px]">
              {campaignTypeLabels.all}
            </ToggleGroupItem>
            <ToggleGroupItem value="install" className="min-w-[100px]">
              {campaignTypeLabels.install}
            </ToggleGroupItem>
            <ToggleGroupItem value="ctv_install" className="min-w-[100px]">
              {campaignTypeLabels.ctv_install}
            </ToggleGroupItem>
          </ToggleGroup>
        </div>

        <div className="flex justify-start space-x-8">
          <div className="grid grid-cols-[repeat(4,260px)] gap-4">
            <div>
              <Label className="font-semibold">Date</Label>
              <CalendarDateRangePicker className="w-full" date={currentDate} setDate={handleDateChange} />
            </div>
            <div>
              <Label className="font-semibold">Audience</Label>
              <Combobox
                value={audience}
                setValue={onAudienceChange}
                isLoading={audienceOptionsLoading}
                disabled={false}
                options={[
                  ...(showAllAudiencesOption ? [{ label: "All Audiences", value: "0" }] : []),
                  ...(audienceOptions
                    ? Object.keys(audienceOptions).map(audienceId => ({
                        label: audienceId + " - " + audienceOptions[audienceId].audience_name,
                        value: audienceId.toString(),
                      }))
                    : []),
                ]}
                buttonPlaceholder="Select audience"
                searchPlaceholder="Search audiences"
                notFoundMessage="No audiences found"
              />
            </div>
            <div>
              <Label className="font-semibold">
                Campaign <QuestionMarkTooltip content="Select a specific campaign or leave empty for all campaigns." />
              </Label>
              <Combobox
                value={campaign}
                setValue={onCampaignChange}
                isLoading={audienceOptionsLoading}
                disabled={false}
                options={
                  audienceOptions &&
                  campaignOptions.map(option => ({
                    label: option.id + " - " + option.name,
                    value: option.id.toString(),
                  }))
                }
                buttonPlaceholder="Select campaign"
                searchPlaceholder="Search campaigns"
                notFoundMessage="No campaigns found"
              />
            </div>
            <div className="flex items-end align-baseline">
              <Button onClick={handleShowButtonClick} disabled={buttonDisabled}>
                {performanceLoading ? <LoadingSpinner /> : "Show"}
              </Button>
            </div>
          </div>
        </div>
      </div>
      <Separator className="my-12" />

      {performanceData &&
        performanceData.conversion_availability &&
        performanceData.conversion_availability.results &&
        performanceData.conversion_availability.results.length > 0 && (
          <PerformanceMetricCards
            revenueData={performanceData.revenue.results}
            primaryStats={primaryStats}
            secondaryStats={secondaryStats}
          />
        )}

      {performanceLoading && <LoadingSpinner />}
      {performanceError && <div className="text-md">Error loading performance data</div>}
      {!performanceData?.conversion_availability?.results?.length && !performanceLoading && shouldFetch && (
        <div className="text-md text-muted-foreground">No conversion data available for the selected filters</div>
      )}

      {performanceData &&
        performanceData.conversion_availability &&
        performanceData.conversion_availability.results &&
        performanceData.conversion_availability.results.length > 0 && (
          <>
            <div className="grid gap-4 xl:grid-cols-2 mb-8">
              <div>
                <RevenueData
                  data={performanceData.revenue.results}
                  date={currentDate}
                  campaign={campaign}
                  audience={audience}
                />
              </div>
              <RevenueByAdPlacementTypeBarChart data={performanceData.revenue_by_ad_type.results} />
            </div>
            <div className="grid gap-4 xl:grid-cols-2 mb-12">
              <CPIAchievement data={performanceData.revenue.results} />
              {hasTargetCpa ? (
                <SecondaryCPAAchievement data={performanceData.revenue.results} />
              ) : (
                <ChartTemplate
                  chart={
                    <div className="flex items-center justify-center h-64 text-muted-foreground">
                      <div className="text-center">
                        <div className="text-lg font-medium mb-2">No Target CPA Set</div>
                        <div className="text-sm">Configure a target CPA to view 2nd AP conversion metrics</div>
                      </div>
                    </div>
                  }
                  table={null}
                  title="CPA Achievement"
                  description="No target CPA configured"
                  summaryMetric="N/A"
                  summaryMetricLabel="CPA Achievement"
                  timeSplittable={false}
                />
              )}
            </div>

            {performanceData?.timing_histogram?.results && performanceData.timing_histogram.results.length > 0 && (
              <div className="grid gap-4 xl:grid-cols-2 mb-8">
                <ConversionTimingHistogramChart
                  data={performanceData.timing_histogram.results}
                  type="primary"
                  medianSeconds={timingStats.apMedian}
                  averageSeconds={timingStats.apAvg}
                />

                {hasTargetCpa ? (
                  <ConversionTimingHistogramChart
                    data={performanceData.timing_histogram.results}
                    type="secondary"
                    medianSeconds={timingStats.secondaryApMedian}
                    averageSeconds={timingStats.secondaryApAvg}
                  />
                ) : (
                  <ChartTemplate
                    chart={
                      <div className="flex items-center justify-center h-64 text-muted-foreground">
                        <div className="text-center">
                          <div className="text-lg font-medium mb-2">No Target CPA Set</div>
                          <div className="text-sm">Configure a target CPA to view 2nd AP conversion timing</div>
                        </div>
                      </div>
                    }
                    table={null}
                    title="Bid to 2nd AP Conversion Time Distribution"
                    description="No target CPA configured"
                    summaryMetric="N/A"
                    summaryMetricLabel="Median Time"
                    timeSplittable={false}
                  />
                )}
              </div>
            )}

            <div className="grid gap-4 xl:grid-cols-2">
              <ConversionAvailabilityChart data={performanceData.conversion_availability.results} type="primary" />

              {hasTargetCpa ? (
                <ConversionAvailabilityChart data={performanceData.conversion_availability.results} type="secondary" />
              ) : (
                <ChartTemplate
                  chart={
                    <div className="flex items-center justify-center h-64 text-muted-foreground">
                      <div className="text-center">
                        <div className="text-lg font-medium mb-2">No Target CPA Set</div>
                        <div className="text-sm">Configure a target CPA to view 2nd AP bid context data</div>
                      </div>
                    </div>
                  }
                  table={null}
                  title="2nd AP Conversions with Bid Context"
                  description="No target CPA configured"
                  summaryMetric="N/A"
                  summaryMetricLabel="Bid Context Availability"
                  timeSplittable={false}
                />
              )}
            </div>
          </>
        )}
    </>
  );
}

export default Performance;
